import React, { useState, useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Plus, 
  MagnifyingGlass, 
  Pencil, 
  Trash,
  UploadSimple,
  Package,
  Eye,
  EyeSlash,
  Folder,
  Rows,
  SquaresFour,
  Tag,
  Eye as EyeIcon
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsPage = () => {
  const { currentStore } = useStore();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDrafts, setShowDrafts] = useState('all'); // 'all' | 'published' | 'drafts'
  const [importModalOpen, setImportModalOpen] = useState(false);
  // Pagination + view
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50); // 20 | 50 | 100 | 9999 (all)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('mv_products_view') || 'table'); // 'table' | 'cards'
  const [sortBy, setSortBy] = useState('updated_desc'); // 'updated_desc' | 'views_desc' | 'price_asc' | 'price_desc' | 'name_asc' | 'stock_asc'

  // Bulk select + bulk-assign categories
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [allCats, setAllCats] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCatIds, setBulkCatIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState('add'); // 'add' | 'replace' | 'remove'
  const [bulkSaving, setBulkSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/categories`, { withCredentials: true })
      .then(r => setAllCats(r.data || []))
      .catch(() => {});
  }, []);
  const toggleSel = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next;
    });
  };
  const toggleSelAll = (visibleIds) => {
    setSelectedIds(prev => {
      const allSelected = visibleIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const toggleBulkCat = (cid) => {
    setBulkCatIds(prev => { const n = new Set(prev); if (n.has(cid)) n.delete(cid); else n.add(cid); return n; });
  };
  const applyBulkCategories = async () => {
    if (!selectedIds.size) return;
    setBulkSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/products/bulk-set-categories`, {
        product_ids: Array.from(selectedIds),
        category_ids: Array.from(bulkCatIds),
        mode: bulkMode,
      }, { withCredentials: true });
      toast.success(`${data.modified} produit(s) mis à jour (${bulkMode})`);
      setBulkOpen(false); setBulkCatIds(new Set()); setSelectedIds(new Set());
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBulkSaving(false); }
  };
  const bulkToggleVisibility = async (publish) => {
    if (!selectedIds.size) return;
    if (!window.confirm(`${publish ? 'Afficher' : 'Masquer'} ${selectedIds.size} produit(s) sur la boutique ?`)) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/products/bulk-visibility`, {
        product_ids: Array.from(selectedIds), is_published: publish,
      }, { withCredentials: true });
      toast.success(`${data.modified} produit(s) ${publish ? 'affichés' : 'masqués'}`);
      setProducts(prev => prev.map(p => selectedIds.has(p.id) ? { ...p, is_published: publish } : p));
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  const fetchProducts = async () => {
    if (!currentStore) return;
    setLoading(true);
    try {
      const skip = (page - 1) * perPage;
      const qs = new URLSearchParams({ limit: String(perPage), skip: String(skip) });
      if (search) qs.append('search', search);
      if (sortBy && sortBy !== 'updated_desc') qs.append('sort', sortBy);
      if (showDrafts === 'on_sale') qs.append('on_sale', 'true');
      const response = await axios.get(`${API_URL}/api/stores/${currentStore.id}/products?${qs.toString()}`, {
        withCredentials: true
      });
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, perPage, sortBy, showDrafts]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore, search, page, perPage, sortBy, showDrafts]);

  useEffect(() => {
    localStorage.setItem('mv_products_view', viewMode);
  }, [viewMode]);

  const handleDelete = async (productId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/stores/${currentStore.id}/products/${productId}`, {
        withCredentials: true
      });
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleVisibility = async (product) => {
    const next = !(product.is_published !== false);
    try {
      await axios.patch(
        `${API_URL}/api/stores/${currentStore.id}/products/${product.id}/visibility`,
        { is_published: next },
        { withCredentials: true },
      );
      toast.success(next ? 'Produit publié' : 'Produit masqué (brouillon)');
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_published: next } : p));
    } catch {
      toast.error('Erreur de mise à jour');
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    const fileInput = e.target.querySelector('input[type="file"]');
    const file = fileInput?.files?.[0];
    
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/stores/${currentStore.id}/import/woocommerce`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success(`${response.data.imported} produits importés`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erreurs lors de l'import`);
      }
      setImportModalOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  if (!currentStore) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Sélectionnez une boutique pour voir les produits</p>
      </div>
    );
  }

  // Stock badge: respect stock_status first; for products with managed stock, color by qty
  const renderStockBadge = (product) => {
    if (product.stock_status !== 'instock') {
      return <Badge className="bg-slate-400 hover:bg-slate-400">Rupture</Badge>;
    }
    const managed = product.manage_stock === true;
    const qty = Number(product.stock_quantity || 0);
    if (managed) {
      if (qty <= 0) return <Badge className="bg-slate-400 hover:bg-slate-400">Rupture</Badge>;
      if (qty <= 3) return <Badge className="bg-red-600 hover:bg-red-600">Stock critique ({qty})</Badge>;
      if (qty <= 10) return <Badge className="bg-amber-500 hover:bg-amber-500">Stock faible ({qty})</Badge>;
      return <Badge className="bg-emerald-600 hover:bg-emerald-600">En stock ({qty})</Badge>;
    }
    return <Badge className="bg-emerald-600 hover:bg-emerald-600">En stock</Badge>;
  };

  // Active promo detection (sale_price set + within date range if dates provided)
  const isOnSaleActive = (p) => {
    if (!p.sale_price || p.sale_price <= 0 || p.sale_price >= p.price) return false;
    const now = new Date();
    if (p.date_on_sale_from && new Date(p.date_on_sale_from) > now) return false;
    if (p.date_on_sale_to && new Date(p.date_on_sale_to) < now) return false;
    return true;
  };

  const fmtDate = (s) => {
    if (!s) return null;
    try { return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }); }
    catch { return null; }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-cabinet text-3xl font-bold tracking-tight">Produits</h1>
          <p className="text-slate-500 mt-1" data-testid="products-count">
            {total > 0
              ? `${Math.min((page - 1) * perPage + 1, total)}–${Math.min(page * perPage, total)} sur ${total} produits`
              : 'Aucun produit'}
          </p>
        </div>
        <div className="flex gap-3">
          <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-btn">
                <UploadSimple className="mr-2" size={18} />
                Import WooCommerce
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importer depuis WooCommerce</DialogTitle>
                <DialogDescription>
                  Importez vos produits depuis un export CSV WooCommerce. Les catégories et attributs seront automatiquement créés.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleImport} className="space-y-4">
                <div className="space-y-2">
                  <Label>Fichier CSV</Label>
                  <Input type="file" accept=".csv" data-testid="import-file" />
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setImportModalOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={importing} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="import-submit">
                    {importing ? 'Import en cours...' : 'Importer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Button className="bg-[#0052FF] hover:bg-[#0040CC]" onClick={() => navigate('/admin/products/new')} data-testid="new-product-btn">
            <Plus className="mr-2" size={18} />
            Nouveau produit
          </Button>
        </div>
      </div>

      {/* Search & filter */}
      <Card className="dashboard-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <Input
                placeholder="Rechercher un produit..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-products"
            />
          </div>
            <div className="flex gap-2 items-center flex-wrap">
              <button onClick={() => setShowDrafts('all')} className={`px-3 py-2 text-[13px] rounded-md border transition-all ${showDrafts === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-testid="filter-all">Tous</button>
              <button onClick={() => setShowDrafts('published')} className={`px-3 py-2 text-[13px] rounded-md border transition-all ${showDrafts === 'published' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-testid="filter-published">Publiés</button>
              <button onClick={() => setShowDrafts('drafts')} className={`px-3 py-2 text-[13px] rounded-md border transition-all ${showDrafts === 'drafts' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-testid="filter-drafts">Brouillons</button>
              <button onClick={() => setShowDrafts('on_sale')} className={`px-3 py-2 text-[13px] rounded-md border transition-all flex items-center gap-1 ${showDrafts === 'on_sale' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-testid="filter-on-sale">
                <Tag size={13} /> En promo
              </button>

              {/* Sort selector */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[170px] h-9" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="updated_desc">Plus récents</SelectItem>
                  <SelectItem value="views_desc">Plus vus</SelectItem>
                  <SelectItem value="price_desc">Prix décroissant</SelectItem>
                  <SelectItem value="price_asc">Prix croissant</SelectItem>
                  <SelectItem value="name_asc">Nom (A→Z)</SelectItem>
                  <SelectItem value="stock_asc">Stock faible d'abord</SelectItem>
                </SelectContent>
              </Select>

              {/* Per-page selector */}
              <Select value={String(perPage)} onValueChange={(v) => setPerPage(Number(v))}>
                <SelectTrigger className="w-[110px] h-9" data-testid="per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                  <SelectItem value="9999">Tous</SelectItem>
                </SelectContent>
              </Select>

              {/* View toggle */}
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 text-[13px] flex items-center gap-1 transition-all ${viewMode === 'table' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid="view-table-btn"
                  title="Vue tableau"
                >
                  <Rows size={16} />
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-2 text-[13px] flex items-center gap-1 transition-all ${viewMode === 'cards' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid="view-cards-btn"
                  title="Vue cartes"
                >
                  <SquaresFour size={16} />
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <Card className="dashboard-card border-2" style={{ borderColor: '#0052FF' }} data-testid="bulk-toolbar">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-semibold text-slate-700">
                {selectedIds.size} produit(s) sélectionné(s)
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear">
                  Désélectionner
                </Button>
                <Button variant="outline" size="sm" onClick={() => bulkToggleVisibility(false)} className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="bulk-hide-products">
                  <EyeSlash size={16} /> Masquer
                </Button>
                <Button variant="outline" size="sm" onClick={() => bulkToggleVisibility(true)} className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-show-products">
                  <Eye size={16} /> Afficher
                </Button>
                <Button onClick={() => setBulkOpen(true)} className="gap-1.5 bg-[#0052FF] hover:bg-[#0040CC]" data-testid="bulk-categories-btn">
                  <Folder size={16} /> Affecter à des catégories
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Categories Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Affecter {selectedIds.size} produit(s) à des catégories</DialogTitle>
            <DialogDescription>
              Choisissez une ou plusieurs catégories puis sélectionnez l'action à appliquer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Action</Label>
              <Select value={bulkMode} onValueChange={setBulkMode}>
                <SelectTrigger data-testid="bulk-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Ajouter (sans toucher aux existantes)</SelectItem>
                  <SelectItem value="replace">Remplacer (efface les anciennes)</SelectItem>
                  <SelectItem value="remove">Retirer ces catégories</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Catégories ({bulkCatIds.size} sélectionnée(s))</Label>
              <div className="max-h-[300px] overflow-y-auto border rounded-lg p-2 space-y-1 mt-1" data-testid="bulk-cat-list">
                {allCats.map(c => (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                    <Checkbox
                      checked={bulkCatIds.has(c.id)}
                      onCheckedChange={() => toggleBulkCat(c.id)}
                      data-testid={`bulk-cat-cb-${c.id}`}
                    />
                    <span className={c.parent_id ? 'pl-4 text-slate-600' : 'font-medium text-slate-800'}>
                      {c.parent_id ? '└ ' : ''}{c.name}
                    </span>
                    <span className="text-[11px] text-slate-400 ml-auto">{c.product_count}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkOpen(false)}>Annuler</Button>
              <Button onClick={applyBulkCategories} disabled={bulkSaving || (bulkMode !== 'remove' && !bulkCatIds.size)} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="apply-bulk-cats">
                {bulkSaving ? 'Application...' : 'Appliquer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Products Table */}
      <Card className="dashboard-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF] mx-auto"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="p-12 text-center">
              <Package size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">Aucun produit pour le moment</p>
              <Button onClick={() => navigate('/admin/products/new')} className="bg-[#0052FF] hover:bg-[#0040CC]">
                <Plus className="mr-2" size={18} />
                Ajouter un produit
              </Button>
            </div>
          ) : viewMode === 'cards' ? (
            // ── Cards view (5 per row) ──
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4" data-testid="products-cards-grid">
              {products
                .filter(p => {
                  if (showDrafts === 'published') return p.is_published !== false;
                  if (showDrafts === 'drafts') return p.is_published === false;
                  return true;
                })
                .map((product) => (
                <div
                  key={product.id}
                  className="group bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-all relative"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="absolute top-2 left-2 z-10">
                    <Checkbox
                      checked={selectedIds.has(product.id)}
                      onCheckedChange={() => toggleSel(product.id)}
                      className="bg-white"
                      data-testid={`select-${product.id}`}
                    />
                  </div>
                  <div className="absolute top-2 right-2 z-10 flex flex-col gap-1 items-end">
                    {product.is_published === false && (
                      <Badge variant="outline" className="text-amber-700 border-amber-400 bg-amber-50/95 text-[10px]">Brouillon</Badge>
                    )}
                    {isOnSaleActive(product) && (
                      <Badge className="bg-rose-600 hover:bg-rose-600 text-[10px]">
                        <Tag size={10} className="mr-0.5" /> Promo
                      </Badge>
                    )}
                    <div className="text-[10px]">{renderStockBadge(product)}</div>
                  </div>
                  <div className="aspect-square bg-slate-100 overflow-hidden">
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={36} className="text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-[13px] leading-tight line-clamp-2 min-h-[2.5rem]">{product.name}</p>
                    {product.categories_resolved?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {product.categories_resolved.slice(0, 2).map(c => (
                          <span key={c.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] bg-slate-100 text-slate-600 border border-slate-200">
                            {c.name}
                          </span>
                        ))}
                        {product.categories_resolved.length > 2 && (
                          <span className="text-[9px] text-slate-400">+{product.categories_resolved.length - 2}</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      {isOnSaleActive(product) ? (
                        <div>
                          <span className="font-bold text-[14px] text-rose-600">{product.sale_price?.toFixed(2)} €</span>
                          <span className="ml-1 text-[11px] text-slate-400 line-through">{product.price?.toFixed(2)} €</span>
                        </div>
                      ) : (
                        <span className="font-bold text-[14px]">{product.price?.toFixed(2)} €</span>
                      )}
                      {product.margin ? (
                        <span className={`text-[11px] font-medium ${product.margin > 30 ? 'text-emerald-600' : product.margin > 15 ? 'text-amber-600' : 'text-red-600'}`}>
                          {product.margin}%
                        </span>
                      ) : null}
                    </div>
                    {isOnSaleActive(product) && (product.date_on_sale_from || product.date_on_sale_to) && (
                      <p className="text-[10px] text-rose-500 mt-1">
                        {fmtDate(product.date_on_sale_from) || '?'} → {fmtDate(product.date_on_sale_to) || '∞'}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1">
                      <span className="flex items-center gap-1"><EyeIcon size={10} /> {product.view_count || 0} vues</span>
                      <span>Vendu 30j: <b className={product.sales_last_month > 0 ? 'text-emerald-600' : ''}>{product.sales_last_month || 0}</b></span>
                    </div>
                    {product.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {product.tags.slice(0, 2).map((t, i) => (
                          <span key={t.id || i} className="inline-flex items-center px-1 py-0.5 rounded text-[9px] bg-blue-50 text-blue-700 border border-blue-100">
                            #{t.name}
                          </span>
                        ))}
                        {product.tags.length > 2 && <span className="text-[9px] text-slate-400">+{product.tags.length - 2}</span>}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleVisibility(product)} title={product.is_published === false ? 'Publier' : 'Masquer'} data-testid={`toggle-visibility-${product.id}`}>
                        {product.is_published === false
                          ? <EyeSlash size={14} className="text-amber-600" />
                          : <Eye size={14} className="text-emerald-600" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/admin/products/${product.id}/edit`)} data-testid={`edit-product-${product.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(product.id)} data-testid={`delete-product-${product.id}`}>
                        <Trash size={14} className="text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={(() => {
                        const visible = products
                          .filter(p => showDrafts === 'all' || (showDrafts === 'published' ? p.is_published !== false : p.is_published === false));
                        return visible.length > 0 && visible.every(p => selectedIds.has(p.id));
                      })()}
                      onCheckedChange={() => {
                        const visibleIds = products
                          .filter(p => showDrafts === 'all' || (showDrafts === 'published' ? p.is_published !== false : p.is_published === false))
                          .map(p => p.id);
                        toggleSelAll(visibleIds);
                      }}
                      data-testid="select-all-cb"
                    />
                  </TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead className="text-right">Prix achat</TableHead>
                  <TableHead className="text-center">Vendu 30j</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Prix</TableHead>
                  <TableHead>Marge</TableHead>
                  <TableHead className="text-center">Vues</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products
                  .filter(p => {
                    if (showDrafts === 'published') return p.is_published !== false;
                    if (showDrafts === 'drafts') return p.is_published === false;
                    return true;
                  })
                  .map((product) => (
                  <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(product.id)}
                        onCheckedChange={() => toggleSel(product.id)}
                        data-testid={`select-${product.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package size={20} className="text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium">{product.name}</p>
                          {product.categories_resolved?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {product.categories_resolved.slice(0, 4).map(c => (
                                <span key={c.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600 border border-slate-200" data-testid={`cat-${c.id}`}>
                                  {c.name}
                                </span>
                              ))}
                              {product.categories_resolved.length > 4 && (
                                <span className="text-[10px] text-slate-400">+{product.categories_resolved.length - 4}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={product.cost_price ? 'font-medium text-slate-700' : 'text-slate-300'}>
                        {product.cost_price ? `${product.cost_price.toFixed(2)} €` : '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={(product.sales_last_month || 0) > 0 ? 'font-semibold text-emerald-600' : 'text-slate-300'}>
                        {product.sales_last_month || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      {product.tags?.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {product.tags.slice(0, 4).map((t, i) => (
                            <span key={t.id || i} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-100">
                              #{t.name}
                            </span>
                          ))}
                          {product.tags.length > 4 && <span className="text-[10px] text-slate-400">+{product.tags.length - 4}</span>}
                        </div>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </TableCell>
                    <TableCell>
                      {isOnSaleActive(product) ? (
                        <div>
                          <p className="font-bold text-rose-600">{product.sale_price?.toFixed(2)} €</p>
                          <p className="text-xs text-slate-400 line-through">{product.price?.toFixed(2)} €</p>
                          {(product.date_on_sale_from || product.date_on_sale_to) && (
                            <p className="text-[10px] text-rose-500 mt-0.5">
                              {fmtDate(product.date_on_sale_from) || '?'} → {fmtDate(product.date_on_sale_to) || '∞'}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="font-medium">{product.price?.toFixed(2)} €</p>
                      )}
                    </TableCell>
                    <TableCell>
                      {product.margin ? (
                        <span className={`font-medium ${product.margin > 30 ? 'text-emerald-600' : product.margin > 15 ? 'text-amber-600' : 'text-red-600'}`}>
                          {product.margin}%
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="inline-flex items-center gap-1 text-slate-500" title="Vues sur la page produit">
                        <EyeIcon size={13} />
                        <span className="font-medium">{product.view_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>{product.stock_quantity}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {renderStockBadge(product)}
                        {isOnSaleActive(product) && (
                          <Badge className="bg-rose-600 hover:bg-rose-600 text-[10px]">
                            <Tag size={10} className="mr-0.5" /> Promo
                          </Badge>
                        )}
                        {product.is_published === false && (
                          <Badge variant="outline" className="text-amber-700 border-amber-400 bg-amber-50 text-[10px]">
                            Brouillon
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleVisibility(product)}
                          title={product.is_published === false ? 'Publier sur le site' : 'Masquer du site (brouillon)'}
                          data-testid={`toggle-visibility-${product.id}`}
                        >
                          {product.is_published === false
                            ? <EyeSlash size={18} className="text-amber-600" />
                            : <Eye size={18} className="text-emerald-600" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/products/${product.id}/edit`)} data-testid={`edit-product-${product.id}`}>
                          <Pencil size={18} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)} data-testid={`delete-product-${product.id}`}>
                          <Trash size={18} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination footer */}
          {!loading && total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t bg-slate-50/50" data-testid="pagination-footer">
              <p className="text-[13px] text-slate-500">
                Page <span className="font-semibold text-slate-800">{page}</span> sur{' '}
                <span className="font-semibold text-slate-800">{Math.max(1, Math.ceil(total / perPage))}</span>
                {' '}— {total} produits au total
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1} data-testid="page-first">««</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="page-prev">Précédent</Button>
                {(() => {
                  const totalPages = Math.max(1, Math.ceil(total / perPage));
                  const pages = [];
                  const startP = Math.max(1, page - 2);
                  const endP = Math.min(totalPages, startP + 4);
                  for (let i = startP; i <= endP; i++) pages.push(i);
                  return pages.map(p => (
                    <Button
                      key={p}
                      size="sm"
                      variant={p === page ? 'default' : 'outline'}
                      className={p === page ? 'bg-[#0052FF] hover:bg-[#0040CC]' : ''}
                      onClick={() => setPage(p)}
                      data-testid={`page-${p}`}
                    >
                      {p}
                    </Button>
                  ));
                })()}
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(Math.ceil(total / perPage), p + 1))} disabled={page >= Math.ceil(total / perPage)} data-testid="page-next">Suivant</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(Math.ceil(total / perPage))} disabled={page >= Math.ceil(total / perPage)} data-testid="page-last">»»</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductsPage;
